import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import en from "@app/i18n/en";

export type ThemePref = "light" | "dark" | "system";
export type Lang = "vi" | "en";

const THEME_KEY = "pcr-theme";
const LANG_KEY = "pcr-lang";

const DICTS: Record<Lang, Record<string, string> | null> = { vi: null, en };

interface PreferencesValue {
  theme: ThemePref;
  isDark: boolean;
  setTheme: (theme: ThemePref) => void;
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (text: string) => string;
}

const PreferencesContext = createContext<PreferencesValue | null>(null);

const read = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // storage may be unavailable (private mode); the choice just won't persist
  }
};

const systemDark = () =>
  window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePref>(
    () => (read(THEME_KEY) as ThemePref) || "system"
  );
  const [lang, setLangState] = useState<Lang>(
    () => (read(LANG_KEY) as Lang) || "vi"
  );
  const [osDark, setOsDark] = useState(systemDark);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");

    if (!mq) return;

    const onChange = (e: MediaQueryListEvent) => setOsDark(e.matches);

    mq.addEventListener("change", onChange);

    return () => mq.removeEventListener("change", onChange);
  }, []);

  const isDark = theme === "dark" || (theme === "system" && osDark);

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      isDark ? "dark" : "light"
    );
  }, [isDark]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setTheme = useCallback((value: ThemePref) => {
    write(THEME_KEY, value);
    setThemeState(value);
  }, []);

  const setLang = useCallback((value: Lang) => {
    write(LANG_KEY, value);
    setLangState(value);
  }, []);

  const t = useCallback(
    (text: string) => DICTS[lang]?.[text] ?? text,
    [lang]
  );

  const value = useMemo(
    () => ({ theme, isDark, setTheme, lang, setLang, t }),
    [theme, isDark, setTheme, lang, setLang, t]
  );

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext);

  if (!ctx) {
    throw new Error("usePreferences must be used inside PreferencesProvider");
  }

  return ctx;
}

/** Shorthand for components that only need translation. */
export function useT() {
  return usePreferences().t;
}
